import { type FC, useState } from "react"
import styled from "styled-components"

import { type PaypalDiscountCode, type StripeDiscountCode } from "@forento/core/models/payment"
import { toPercentString, toPriceString } from "@forento/core/utilities/currency"
import dayjs from "@forento/core/utilities/dayjs"
import { toCountFormat } from "@forento/core/utilities/number"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { DeleteIcon } from "~/components/Icon"
import Layout, { PageHeader, PageTitle } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import { dangerColor } from "~/utilities/styles"
import { query } from "~/utilities/trpc"

import CreateCodeModal from "./CreateCodeModal"

const DiscountCodesPage: FC = () => {
	const platform = usePlatform()!

	const { data: discountCodes, error, refetch } = query.payment.discountCode.list.useQuery()

	const [isCreateCodeModalOpen, setCreateCodeModalOpen] = useState(false)

	const hasInvalidStripeCodes =
		discountCodes?.type === "stripe" &&
		discountCodes.codes.some(x => x.duration.type === "forever" && x.discount.type === "amount")

	return (
		<Layout>
			<CreateCodeModal
				isOpen={isCreateCodeModalOpen}
				onSubmit={async () => {
					await refetch()
					setCreateCodeModalOpen(false)
				}}
				onClose={() => setCreateCodeModalOpen(false)}
			/>
			<PageHeader>
				<PageTitle>Discount Codes</PageTitle>
				<Button variant="primary" onClick={() => setCreateCodeModalOpen(true)}>
					Create new
				</Button>
			</PageHeader>
			{hasInvalidStripeCodes && (
				<Warning>
					We apologize for any inconvenience, but Stripe has recently discontinued support for fixed-amount
					discount codes that have no end date. As a result, any existing codes with these properties are now
					invalid.
				</Warning>
			)}
			{platform.paymentGateway?.type === "paypal" && (
				<Text>
					Due to PayPal's lack of support for discount codes, they are unfortunately only available for
					product purchases and not for membership subscriptions.
				</Text>
			)}
			{error ? (
				<p>Failed to load discount codes.</p>
			) : discountCodes === undefined ? (
				<PartialLoadingPage />
			) : discountCodes.type === "stripe" ? (
				<StripeCodes codes={discountCodes.codes} reload={refetch} />
			) : discountCodes.type === "paypal" ? (
				<PaypalCodes codes={discountCodes.codes} reload={refetch} />
			) : (
				exhaustiveGuard(discountCodes)
			)}
		</Layout>
	)
}

const StripeCodes: FC<{ codes: StripeDiscountCode[]; reload(): Promise<unknown> }> = ({ codes, reload }) => {
	const platform = usePlatform()!
	const toast = useToast()
	const alert = useAlert()

	const remove = query.payment.discountCode.deleteStripe.useMutation({
		async onSuccess() {
			await reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to delete discount code. Please try again.")
		},
	})

	return (
		<Table
			emptyText="You haven't created any discount codes yet."
			columns={["Name", "Code", "Discount", "Duration", "Redemptions", "Expires", "Manage"]}
			data={codes.map(code => [
				code.duration.type === "forever" && code.discount.type === "amount" ? (
					<InvalidCode key={code.id}>{code.name}</InvalidCode>
				) : (
					code.name
				),
				code.code,
				code.discount.type === "amount"
					? toPriceString({ amount: code.discount.value, currency: platform.currency })
					: toPercentString(code.discount.value),
				code.duration.type === "once"
					? "Once"
					: code.duration.type === "forever"
						? "Forever"
						: code.duration.type === "repeating"
							? `${code.duration.months} months`
							: null,
				code.maxRedemptions ?? "Unlimited",
				code.lastRedemptionDate !== null ? dayjs(code.lastRedemptionDate).format("YYYY-MM-DD HH:mm") : "Never",
				<Actions key={code.id}>
					<Tooltip tooltip="Delete">
						<DeleteButton
							onClick={async () => {
								const dialog = await alert.confirm(
									"Delete discount code",
									`Are you sure you want to delete the discount code **${code.code}**? Customers will no longer be able to use it.`,
								)
								if (!dialog.result) return
								try {
									await remove.mutateAsync(code.id)
								} catch {
									// onError has already shown the failure.
								}
								dialog.close()
							}}
							ariaLabel={`Delete ${code.name}`}
							isDisabled={remove.isPending}
							isLoading={remove.isPending && remove.variables === code.id}
							replaceOnLoading
						>
							<DeleteIcon />
						</DeleteButton>
					</Tooltip>
				</Actions>,
			])}
		/>
	)
}

const PaypalCodes: FC<{ codes: PaypalDiscountCode[]; reload(): Promise<unknown> }> = ({ codes, reload }) => {
	const platform = usePlatform()!
	const toast = useToast()
	const alert = useAlert()

	const remove = query.payment.discountCode.deletePaypal.useMutation({
		async onSuccess() {
			await reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to delete discount code. Please try again.")
		},
	})

	return (
		<Table
			emptyText="You haven't created any discount codes yet."
			columns={["Name", "Code", "Discount", "Redemptions (C/M)", "Expires", "Manage"]}
			data={codes.map(code => [
				code.name,
				code.code,
				code.discount.type === "amount"
					? toPriceString({ amount: code.discount.value, currency: platform.currency })
					: toPercentString(code.discount.value),
				`${toCountFormat(code.redemptions)}/${code.maxRedemptions ? toCountFormat(code.maxRedemptions) : "∞"}`,
				code.lastRedemptionDate !== null ? dayjs(code.lastRedemptionDate).format("YYYY-MM-DD HH:mm") : "Never",
				<Actions key={code.id}>
					<Tooltip tooltip="Delete">
						<DeleteButton
							onClick={async () => {
								const dialog = await alert.confirm(
									"Delete discount code",
									`Are you sure you want to delete the discount code **${code.code}**? Customers will no longer be able to use it.`,
								)
								if (!dialog.result) return
								try {
									await remove.mutateAsync(code.id)
								} catch {
									// onError has already shown the failure.
								}
								dialog.close()
							}}
							ariaLabel={`Delete ${code.name}`}
							isDisabled={remove.isPending}
							isLoading={remove.isPending && remove.variables === code.id}
							replaceOnLoading
						>
							<DeleteIcon />
						</DeleteButton>
					</Tooltip>
				</Actions>,
			])}
		/>
	)
}

const Text = styled.p`
	margin-bottom: 16px;
`

const Warning = styled.p`
	margin-bottom: 16px;
	background-color: #ffe0b2;
	padding: 16px 24px;
	border-radius: 8px;
`

const InvalidCode = styled.p`
	color: ${dangerColor};
	text-decoration: line-through;
`

const Actions = styled.div`
	display: flex;
`

const DeleteButton = styled(Button)`
	width: 24px;
	height: 24px;
	color: ${dangerColor};
`

export default DiscountCodesPage
